import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();

  const [status, setStatus]               = useState("loading"); // loading | success | error
  const [paymentDetails, setPaymentDetails] = useState(null);
  // credit-polling state: idle | polling | redirecting | fallback
  const [creditState, setCreditState]     = useState("idle");

  const paymentType = searchParams.get("type");
  const returnClass = searchParams.get("return_class");
  const isClassPay  = paymentType === "classes" && !!returnClass;

  // Stable refs — don't trigger re-renders
  const initialCreditsRef = useRef(null);
  const creditTimerRef    = useRef(null);

  // Auth helpers from localStorage (written at login)
  const getMemberId = () => {
    try {
      const d = localStorage.getItem("memberData");
      return d ? JSON.parse(d).id : null;
    } catch { return null; }
  };
  const getAuthHeaders = () => {
    const t = localStorage.getItem("memberToken");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // ── Payment-status polling (unchanged logic) ───────────────────────────────
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
    return () => {
      if (creditTimerRef.current) clearInterval(creditTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) { setStatus("error"); return; }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentDetails(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");

        // Analytics dedup guard
        const dedupKey = "nbc_px_" + sessionId;
        if (!sessionStorage.getItem(dedupKey)) {
          sessionStorage.setItem(dedupKey, "1");
          const amount   = (response.data.amount_total || 0) / 100;
          const itemName = response.data.plan_name || response.data.package_type || "NBC Purchase";
          if (window.fbq) window.fbq("track", "Purchase", {
            value: amount, currency: "USD", content_name: itemName,
          });
          if (window.gtag) window.gtag("event", "purchase", {
            transaction_id: sessionId, value: amount, currency: "USD",
            items: [{ item_name: itemName, price: amount, quantity: 1 }],
          });
        }

        toast.success("Payment successful!");

        // For single-class purchases with a return target: poll for credit applied
        if (isClassPay) {
          startCreditPolling();
        }
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  // ── Credit-balance polling ─────────────────────────────────────────────────
  // Records balance before the webhook fires, then redirects once it goes up.
  const startCreditPolling = async () => {
    const memberId = getMemberId();
    if (!memberId) {
      // No member id — redirect without polling
      navigate(`/dashboard/book-classes?book=${returnClass}`);
      return;
    }

    setCreditState("polling");

    // Snapshot the current balance (before webhook credit is applied)
    try {
      const snap = await axios.get(`${API}/members/${memberId}`, { headers: getAuthHeaders() });
      initialCreditsRef.current = snap.data.class_credits || 0;
    } catch {
      initialCreditsRef.current = 0;
    }

    let elapsed = 0;
    const INTERVAL = 1500; // ms between checks
    const MAX_MS   = 20000; // give up after 20 s

    creditTimerRef.current = setInterval(async () => {
      elapsed += INTERVAL;
      try {
        const res     = await axios.get(`${API}/members/${memberId}`, { headers: getAuthHeaders() });
        const current = res.data.class_credits || 0;
        if (current > initialCreditsRef.current) {
          clearInterval(creditTimerRef.current);
          setCreditState("redirecting");
          navigate(`/dashboard/book-classes?book=${returnClass}`);
          return;
        }
      } catch { /* keep polling */ }

      if (elapsed >= MAX_MS) {
        clearInterval(creditTimerRef.current);
        setCreditState("fallback");
      }
    }, INTERVAL);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <img src="/nbc_logo.jpg" alt="NBC" className="h-16 mx-auto mb-8" />

        {/* ── Loading ── */}
        {status === "loading" && (
          <div className="animate-fade-in">
            <Loader2 className="w-16 h-16 text-[#FFB612] mx-auto animate-spin mb-6" />
            <h1 className="text-2xl font-bold uppercase mb-2">Processing Payment</h1>
            <p className="text-white/60">Please wait while we confirm your payment...</p>
          </div>
        )}

        {/* ── Success ── */}
        {status === "success" && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold uppercase mb-2">Payment Successful!</h1>

            {/* Single-class purchase with return target */}
            {isClassPay && (creditState === "polling" || creditState === "redirecting") && (
              <>
                <p className="text-white/60 mb-4">Booking your class...</p>
                <Loader2 className="w-8 h-8 text-[#FFB612] mx-auto animate-spin" />
              </>
            )}

            {/* Credit polling timed out — fallback tap link */}
            {isClassPay && creditState === "fallback" && (
              <>
                <p className="text-white/60 mb-6">
                  Payment received! Tap below to book your class.
                </p>
                <Button
                  onClick={() => navigate(`/dashboard/book-classes?book=${returnClass}`)}
                  className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase w-full"
                  data-testid="fallback-book-btn"
                >
                  Book Your Class
                </Button>
              </>
            )}

            {/* All other payment types (membership, diet plan, merch, etc.) */}
            {!isClassPay && (
              <>
                <p className="text-white/60 mb-8">
                  Welcome to the NBC family. Your membership is now active.
                </p>

                {paymentDetails && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-8 text-left">
                    <h3 className="font-bold uppercase text-sm mb-4 text-[#FFB612]">
                      Transaction Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/50">Amount</span>
                        <span className="font-mono">
                          ${(paymentDetails.amount_total / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Status</span>
                        <span className="text-green-400">Paid</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase w-full"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>

                {/* Diet plan upsell intent */}
                {sessionStorage.getItem("nbc_dietplan_intent") && (
                  <div className="mt-4 border border-[#00E5FF]/40 p-4 text-center">
                    <p className="text-white text-sm mb-3">
                      Your membership is active! Add Custom Diet Plan for $9.99/month.
                    </p>
                    <button
                      onClick={() => {
                        sessionStorage.removeItem("nbc_dietplan_intent");
                        navigate("/dashboard/diet-plan");
                      }}
                      className="w-full py-3 font-black uppercase tracking-widest text-black text-sm"
                      style={{ background: "#00E5FF" }}
                      data-testid="payment-success-diet-btn"
                    >
                      Add Custom Diet Plan &gt;
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Error ── */}
        {status === "error" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">!</span>
            </div>
            <h1 className="text-2xl font-bold uppercase mb-2">Payment Issue</h1>
            <p className="text-white/60 mb-8">
              We couldn&apos;t confirm your payment. Please check your email or contact support.
            </p>
            <div className="space-y-4">
              <Button
                onClick={() => navigate("/")}
                className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase w-full"
              >
                Return Home
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="border-white/20 text-white w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
